import type {
  PermissionsResponse,
  PermissionFlags,
  PermissionIndex,
  CanParams,
} from "@/types/permissions";

const toSlug = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

// Always use names to derive keys (ignore content_type)
const keyOf = (name: string, _contentType: number | null) =>
  `slug:${toSlug(name)}`;

const flagsOf = (p: {
  can_view: boolean;
  can_add: boolean;
  can_delete: boolean;
  can_change: boolean;
  can_export: boolean;
}): PermissionFlags => ({
  view: !!p.can_view,
  add: !!p.can_add,
  delete: !!p.can_delete,
  change: !!p.can_change,
  export: !!p.can_export,
});

export const buildPermissionIndex = (
  resp: PermissionsResponse
): PermissionIndex => {
  const byKey: PermissionIndex["byKey"] = {};

  for (const p of resp.permissions) {
    const parentKey = keyOf(p.name, p.content_type);
    const subs: Record<string, PermissionFlags> = {};

    for (const sp of p.sub_model_permissions ?? []) {
      const subKey = keyOf(sp.name, sp.content_type);
      subs[subKey] = flagsOf(sp);
    }

    byKey[parentKey] = {
      flags: flagsOf(p),
      subs,
    };
  }

  return { byKey, groups: resp.groups ?? {} };
};

// Resolve by name only (string). Numbers will be stringified and slugged
const keyFromParam = (value: string | number) =>
  `slug:${toSlug(String(value))}`;

export const makePermissionAPI = (index: PermissionIndex) => {
  const has = ({ resource, action, subResource }: CanParams): boolean => {
    const resKey = keyFromParam(resource);
    const entry = index.byKey[resKey];

    if (!entry) return false;

    if (subResource != null) {
      const subKey = keyFromParam(subResource);
      const sub = entry.subs[subKey];
      return sub ? !!sub[action] : false;
    }

    return !!entry.flags[action];
  };

  const hasAny = (checks: CanParams[]): boolean => {
    for (const c of checks) {
      if (has(c)) return true;
    }
    return false;
  };

  const hasAll = (checks: CanParams[]): boolean => {
    for (const c of checks) {
      if (!has(c)) return false;
    }
    return true;
  };

  return { has, hasAny, hasAll };
};
